import { useTranslations } from "next-intl";
import type { ProfileType, Gender } from "@/lib/types";
import { cn } from "@/lib/cn";
import { Input, Textarea } from "@/components/ui";
import {
  ageFromBirthDate,
  maxBirthDate,
  type FieldErrors,
  type PartnerDraft,
} from "../schema";

/** `common.profileTypes` key for a partner gender. */
const GENDER_TYPE_KEY: Record<Gender, "female" | "male"> = { f: "female", m: "male" };

function GenderToggle({
  value,
  onChange,
}: {
  value: Gender;
  onChange: (g: Gender) => void;
}) {
  const tTypes = useTranslations("common.profileTypes");
  return (
    <div className="inline-flex rounded-xl border border-white/10 bg-white/[0.03] p-1">
      {(["f", "m"] as Gender[]).map((g) => (
        <button
          key={g}
          type="button"
          onClick={() => onChange(g)}
          aria-pressed={value === g}
          className={cn(
            "rounded-lg px-4 py-1.5 text-sm font-medium transition-colors",
            value === g
              ? "bg-flamingo/15 text-flamingo"
              : "text-white/55 hover:text-white/80",
          )}
        >
          {tTypes(GENDER_TYPE_KEY[g])}
        </button>
      ))}
    </div>
  );
}

function PartnerPanel({
  index,
  label,
  partner,
  editableGender,
  errors,
  onChange,
}: {
  index: number;
  label: string;
  partner: PartnerDraft;
  editableGender: boolean;
  errors: FieldErrors;
  onChange: (patch: Partial<PartnerDraft>) => void;
}) {
  const t = useTranslations("join.partners");
  const tJoin = useTranslations("join");
  const tTypes = useTranslations("common.profileTypes");
  const age = ageFromBirthDate(partner.birthDate);
  const birthErrKey = errors[`partner.${index}.birthDate`];

  return (
    <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-5">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h3 className="font-display text-base font-semibold text-white">{label}</h3>
        {editableGender ? (
          <GenderToggle value={partner.gender} onChange={(g) => onChange({ gender: g })} />
        ) : (
          <span className="rounded-lg bg-white/[0.05] px-3 py-1 text-sm text-white/60">
            {tTypes(GENDER_TYPE_KEY[partner.gender])}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-4">
        <Input
          type="date"
          label={t("dobLabel")}
          required
          max={maxBirthDate()}
          value={partner.birthDate}
          onChange={(e) => onChange({ birthDate: e.target.value })}
          error={birthErrKey ? tJoin(birthErrKey) : undefined}
          hint={
            !birthErrKey && age !== null && age >= 18
              ? t("ageHint", { age })
              : t("dobHint")
          }
        />
        <Textarea
          label={t("bioLabel")}
          hint={t("bioHint")}
          rows={2}
          maxLength={280}
          value={partner.bio}
          onChange={(e) => onChange({ bio: e.target.value })}
        />
      </div>
    </div>
  );
}

export function PartnersStep({
  profileType,
  partners,
  errors,
  onChange,
}: {
  profileType: ProfileType;
  partners: PartnerDraft[];
  errors: FieldErrors;
  onChange: (index: number, patch: Partial<PartnerDraft>) => void;
}) {
  const t = useTranslations("join.partners");
  const isCouple = profileType === "couple";

  return (
    <div>
      <h1 className="font-display text-2xl font-semibold text-white sm:text-3xl">
        {isCouple ? t("titleCouple") : t("titleSingle")}
      </h1>
      <p className="mt-2 text-sm text-white/55">
        {t("subtitle")}
      </p>

      <div
        className={cn(
          "mt-6 grid gap-4",
          isCouple && "sm:grid-cols-2",
        )}
      >
        {partners.map((partner, i) => (
          <PartnerPanel
            key={i}
            index={i}
            label={isCouple ? t("partnerLabel", { n: i + 1 }) : t("youLabel")}
            partner={partner}
            editableGender={isCouple}
            errors={errors}
            onChange={(patch) => onChange(i, patch)}
          />
        ))}
      </div>
    </div>
  );
}
